import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { SessionService } from 'src/app/services/shared/session/session.service';

import { ThemeService } from '../../../layout/services/theme.service';

/**
 * Glass "Session expired" dialog — the admin-v3 replacement for admin-v2's
 * `SessionExpiredDialogComponent` (a `p-dialog`). Purely a view over the shared
 * root `SessionService`: it shows whenever `dialogVisible()` is set (by the auth
 * guard or the HTTP interceptor) and offers a silent refresh (grace period) or a
 * password re-login (fully expired), then `SessionService` resolves the pending
 * navigation / retries the failed request.
 *
 * **Mounted in `AppComponent`, not `AdminV3LayoutComponent`** (unlike
 * `ToastComponent`): `adminV2AuthGuard` runs before any route layout mounts, so a
 * layout-hosted dialog would never render on a cold navigation and the guard's
 * `await checkSession()` would hang forever (the white-screen bug this fixes).
 * The component renders its own `<div class="yl-app" data-theme>` token scope so
 * the glass kit resolves outside the layout.
 */
@Component({
    selector: 'app-yl-session-dialog',
    standalone: true,
    imports: [FormsModule],
    templateUrl: './session-dialog.component.html',
    styleUrl: './session-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionDialogComponent {
    protected readonly session = inject(SessionService);
    protected readonly theme = inject(ThemeService);

    protected password = '';

    protected onRefresh(): void {
        this.session.refreshSession();
    }

    protected onRelogin(): void {
        if (!this.password.trim() || this.session.isProcessing()) return;
        this.session.reloginWithPassword(this.password);
    }

    protected onLogout(): void {
        this.session.logout();
    }
}
